// Single source of truth for the app clock.
//
// Every date the app SHOWS or BUCKETS BY DAY must go through these helpers so
// all users see identical reports regardless of their device timezone. The
// business runs on Ottawa time (IANA: America/Toronto — Ottawa shares Toronto's
// zone). Without this, a viewer in another timezone slices "today" at a
// different midnight and daily totals diverge between machines.

import { dayLabelKind, formatDayLabel } from './chat/dayLabel';

export const APP_TIME_ZONE = 'America/Toronto';

// Intl.DateTimeFormat construction is expensive (~0.1ms); formatting with a
// cached instance is microseconds. All formatters are cached at module level
// because report pages format thousands of timestamps per render.
const ymdFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});
const timeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE, hour: '2-digit', minute: '2-digit',
});
const dateLabelFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE, month: 'short', day: 'numeric', year: 'numeric',
});
const dateWeekdayLabelFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
});
const fmtCache = new Map<string, Intl.DateTimeFormat>();

/** 'YYYY-MM-DD' of a timestamp in Ottawa time. This is THE day-bucketing key. */
export const appDateStr = (ts: number | Date): string => ymdFmt.format(ts);

/** Today's 'YYYY-MM-DD' in Ottawa time. */
export const appTodayStr = (): string => ymdFmt.format(Date.now());

/** 'hh:mm AM/PM' in Ottawa time. */
export const appTime = (ts: number | Date): string => timeFmt.format(ts);

/** 'Aug 23, 2026' in Ottawa time. */
export const appDateLabel = (ts: number | Date): string => dateLabelFmt.format(ts);

/** 'Sat, Aug 23, 2026' in Ottawa time. */
export const appDateWeekdayLabel = (ts: number | Date): string => dateWeekdayLabelFmt.format(ts);

/**
 * Ottawa-pinned replacement for ad-hoc toLocaleDateString/toLocaleTimeString
 * calls. Formatters are cached per options object shape.
 */
export const appFormat = (ts: number | Date, opts: Intl.DateTimeFormatOptions): string => {
    const key = JSON.stringify(opts);
    let f = fmtCache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', { timeZone: APP_TIME_ZONE, ...opts });
        fmtCache.set(key, f);
    }
    return f.format(ts);
};

const chatWeekdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: APP_TIME_ZONE, weekday: 'long' });

/**
 * THE chat day-divider label, Ottawa-pinned: 'Today' / 'Yesterday' / the
 * weekday name for the rest of the last seven days / 'Apr 21, 2026' beyond.
 *
 * This is the `dayDividerLabel` the direct/order chat has shipped with, lifted
 * here so group chat and the SMS Text Inbox stop carrying their own copies
 * (ForwardedInbox's `fmtDay` bypassed appTime entirely and printed
 * '21 April 2026'). The wording is Umer's call, 2026-09-14 (FINAL): keep
 * exactly what staff already read every day.
 *
 * Added identically to wolfdriver-mobile/services/appTime.ts — the two apps
 * show the same transcript, so the dividers have to read the same. The
 * calendar decision lives in the mirrored services/chat/dayLabel.ts; this
 * function only supplies Ottawa-time day keys and the formatted words.
 *
 * Passing `nowMs` explicitly keeps it testable and lets a list compute one
 * 'now' for every divider instead of drifting across a render.
 */
export const appChatDayLabel = (ts: number | Date, nowMs: number | Date = Date.now()): string => {
    const kind = dayLabelKind(appDateStr(ts), appDateStr(nowMs));
    if (kind === 'today' || kind === 'yesterday') return formatDayLabel(kind, { weekday: '', date: '' });
    return formatDayLabel(kind, {
        weekday: kind === 'weekday' ? chatWeekdayFmt.format(ts) : '',
        date: kind === 'date' ? appDateLabel(ts) : '',
    });
};

/**
 * Pure calendar math on a 'YYYY-MM-DD' string — timezone-free by construction
 * (uses UTC internally only as an inert calendar). Use for prev/next-day
 * stepping and range presets instead of mutating local Date objects.
 */
export const addDaysToYmd = (ymd: string, days: number): string => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const offsetProbeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
});

// Offset (ms) of Ottawa time relative to UTC at the given instant (handles DST).
const tzOffsetMs = (utcMs: number): number => {
    const parts = offsetProbeFmt.formatToParts(utcMs);
    const get = (t: string) => Number(parts.find(p => p.type === t)?.value || 0);
    // hour12:false can yield "24" at midnight — normalize.
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return asUtc - utcMs;
};

/**
 * Epoch ms of 00:00:00.000 of the given 'YYYY-MM-DD' in Ottawa time.
 * Use for range boundaries in Firestore timestamp queries — never
 * `new Date(ymd)` + setHours, which uses the device timezone.
 */
export const appDayStartMs = (ymd: string): number => {
    const [y, m, d] = ymd.split('-').map(Number);
    const utcGuess = Date.UTC(y, m - 1, d);
    // Two passes handle DST transitions where the offset at the guess differs
    // from the offset at the true local midnight.
    const ts = utcGuess - tzOffsetMs(utcGuess);
    return utcGuess - tzOffsetMs(ts);
};

/** Epoch ms of 23:59:59.999 of the given 'YYYY-MM-DD' in Ottawa time. */
export const appDayEndMs = (ymd: string): number => appDayStartMs(addDaysToYmd(ymd, 1)) - 1;
